<template>
  <div>
    <PeriodFilters @apply="applyFilters" />
    <div class="main-content rounded-md mt-2 border-0">
      <CalendarActionsWrapper
        :calendar-date="filters.filterBy === 'Week' ? dateRange : dateRange.startDate"
        :is-concise-view="true"
        :filter-view="filters.filterBy"
        :filters="['Month', 'Week', 'Day']"
        @selectDate="setDate"
        @selectDateRange="setDateRange"
        @setFilter="setFilter"
        @applyFilter="applyFilter"
      ></CalendarActionsWrapper>
      <template v-if="isLoading"><Loader class="mt-20 mb-40" :content="true" /></template>
      <div v-else class="mt-2">
        <UiCalendar
          v-if="filters.filterBy === 'Month'"
          :key="forceReRender"
          :calendar-date="dateRange.startDate"
          class="bg-primary-white"
          :events="events"
        >
          <template v-slot="{ events: slotEvents }">
            <div v-for="(event, index) in slotEvents" :key="index">
              <div
                v-if="index < 3"
                class="time-schedule flex cursor-pointer justify-center items-center text_size whitespace-nowrap gap-0.5 text-xs"
                @click="singleRecord(event)"
              >
                <p
                  class="flex-shrink-0 dot dot_size h-2 w-2 rounded-full"
                  :style="{ backgroundColor: event.subject.color }"
                ></p>
                <div
                  class="text-white px-1 mb-0.5 rounded-sm"
                  :style="
                    calculatePresentRate(event.attendanceMeta.present, event.attendanceMeta.absent)
                  "
                >
                  {{ `${localTime(event.startTime, true)} - ${localTime(event.endTime, true)}` }}
                </div>
              </div>
            </div>
            <div v-if="slotEvents.length > 3" class="time-schedule viewMore">
              <p
                class="font-roboto font-medium text-primary-grey-light cursor-pointer viewMoreText"
                @click="clickDay(slotEvents[0].date)"
              >
                {{ $t('schedule.View More') + ' ...' }}
              </p>
            </div>
          </template>
        </UiCalendar>
        <ScheduleWeeklyView
          v-if="filters.filterBy === 'Week'"
          :theads="dayRange"
          :table-time="[timeRange[0] || '08:00 am', timeRange[1] || '05:00 pm']"
          :events="events"
          :hide-actions="!!activeStudent || !!isMyCurrentTimeTable"
          :actions="['edit', 'delete']"
          @viewEvent="viewEvent"
          @editEvent="editEvent"
          @deleteEvent="deleteEvent"
        />
        <DailyView
          v-if="filters.filterBy === 'Day'"
          :key="currentDay"
          :theads="[currentDay]"
          :table-time="[timeRange[0] || '09:00 am', timeRange[1] || '05:00 pm']"
          :events="events"
          :hide-actions="!!activeStudent || !!isMyCurrentTimeTable"
          @viewEvent="viewEvent"
          @editEvent="editEvent"
          @deleteEvent="deleteEvent"
        />
      </div>
    </div>
    <AddEditPeriodModal
      :modal-show="showModal"
      :edit-data="editEventData"
      @close="handleModalClose"
      @saved="handlePeriodSaved"
    />

    <PeriodDetailModal
      :modal-show="PeriodDetailModal"
      :details-events-arr="detailsEventsArr"
      @close="PeriodDetailModal = false"
    />
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import isEmpty from 'lodash/isEmpty'
import UiCalendar from '@components/Calendar/index.vue'
import CalendarActionsWrapper from '@components/Calendar/CalendarActionsWrapper.vue'
import Loader from '@components/BaseComponent/Loader.vue'
import ScheduleWeeklyView from '@components/UiElements/Scheduler/Schedule.vue'
import FILTER_KEYS from '@src/constants/filter-constants.js'
import DailyView from '@/src/router/views/time-table/current-time-table/PeriodDailyView.vue'
import PeriodDetailModal from '@/src/router/views/time-table/current-time-table/PeriodDetailModal.vue'
import AddEditPeriodModal from '@/src/router/views/time-table/current-time-table/AddEditPeriodModal.vue'
import { TENANT_ROLES } from '@src/constants/user-roles-constants.js'
import {
  getMonthStartAndEndDate,
  formatDateFromLocalToUTC,
  getStartTimeOfTheDay,
  getEndTimeOfTheDay,
} from '@utils/moment.util'
import { objectDeepCopy } from '@/src/utils/generalUtil'
import PeriodFilters from '@src/router/views/time-table/current-time-table/PeriodFilters.vue'
import timeMixin from '@src/mixins/timeMixin'
import timeTableMixin from '@src/mixins/components/timetable-mixin'
import { getSubjectEventUtc, getTimeRange, getEventsWithNonOverlap } from '@utils/timeTableUtils'
import GENERAL_CONSTANTS from '@src/constants/general-constants.js'
import ROUTES_CONSTANTS from '@/src/constants/route-constants.js'

export default {
  name: 'CurrentTimeTableView',
  components: {
    PeriodFilters,
    Loader,
    ScheduleWeeklyView,
    CalendarActionsWrapper,
    DailyView,
    UiCalendar,
    PeriodDetailModal,
    AddEditPeriodModal,
  },
  mixins: [timeMixin, timeTableMixin],
  data() {
    return {
      isEmpty,
      GENERAL_CONSTANTS,
      isLoading: false,
      filtersData: {},
      forceReRender: 1,
      editEventData: null,
      filters: {
        filterBy: 'Month',
      },
      get currentStudent() {
        return JSON.parse(localStorage.getItem('currentActiveStudent')) || null
      },
      dateRange: {},
      dayRange: ['monday', 'sunday'],
      timeRange: ['0:00 am', '0:00 am'],
      events: [],
      currentDay: 'Monday',
      respDataObjDump: null,
      PeriodDetailModal: false,
      detailsEventsArr: [],
    }
  },
  page: {
    title: 'Current Time Table',
    meta: [
      {
        name: 'description',
        content: ' Automated schedule of class ',
      },
    ],
  },
  computed: {
    ...mapState({
      activeStudent: (state) => state.student.activeStudent,
      activeRole: (state) => state.layout.activeRole,
      currentUser: (state) => state.auth.currentUser,
      showModal: (state) => state.layout.showModal,
      currentSectionScope: (state) => state.layout.currentSectionScope.id,
    }),
    isMyCurrentTimeTable() {
      return this.$route.name === ROUTES_CONSTANTS.TIME_TABLE.MY_TIMETABLE.PATH
    },

    ...mapGetters('layout', ['campusTimeZone']),
  },
  watch: {
    events: {
      deep: true,
      immediate: true,
      handler() {
        const timeRangeResult = getTimeRange(this.events)
        this.timeRange = Array.isArray(timeRangeResult) ? timeRangeResult : ['0:00 am', '0:00 am']
      },
    },
    currentSectionScope: {
      handler() {
        if (this.currentSectionScope) {
          this.setDayRange(this.dateRange)
          this.applyFilter()
        } else {
          this.setStaticRightBar()
        }
      },
    },
    isMyCurrentTimeTable: {
      handler() {
        this.setStaticRightBar()
      },
    },
  },
  mounted() {
    this.setStaticRightBar()
    if (this.currentSectionScope || this.isMyCurrentTimeTable) {
      this.setDayRange(this.dateRange)
      this.applyFilter()
    }
  },
  created() {
    const startDate = new Date()
    const endDate = new Date()
    endDate.setDate(endDate.getDate() + 6)
    this.dateRange = { startDate, endDate }
  },

  methods: {
    applyFilters(filtersPayload) {
      this.filtersData = objectDeepCopy(filtersPayload)
      this.applyFilter()
    },
    setStaticRightBar() {
      this.setRightbarContent({
        header: {
          title: 'PERIOD',
          buttons:
            this.currentStudent || this.isMyCurrentTimeTable
              ? []
              : [
                  {
                    title: 'ADD_CURRENT_TIME_TABLE',
                    classes: ['primary-button-right'],
                    permissions: { roles: ['super_admin', 'campus_admin', 'section_teacher'] },
                  },
                ],
        },
        stats: [
          {
            title: GENERAL_CONSTANTS.STATS_AND_SUMMARY,
            categories: [
              { text: 'Total Subject', value: '--' },
              { text: 'TODAYS_PERIODS', value: '--' },
            ],
          },
        ],
      })
    },
    setDateRange(dateRange) {
      this.dateRange = dateRange
    },

    setDate(date) {
      this.dateRange.startDate = date
    },

    setDayRange({ startDate }) {
      const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
      const dayIndex = new Date(startDate).getDay()
      this.dayRange[0] = days[dayIndex]
      this.dayRange[1] = days[dayIndex + 6 > 6 ? dayIndex - 1 : dayIndex + 6]
    },

    applyFilter() {
      if (!this.currentSectionScope && !this.isMyCurrentTimeTable) return
      this.forceReRender++
      const startDate = formatDateFromLocalToUTC(this.dateRange.startDate)
      const endDate = formatDateFromLocalToUTC(this.dateRange.endDate)
      this.currentDay = formatDateFromLocalToUTC(startDate, 'dddd')
      this.setDayRange(this.dateRange)

      let startTime = {}
      if (this.filters.filterBy === 'Week') {
        startTime = {
          [FILTER_KEYS.GREATER_THAN_EQUAL]: `${getStartTimeOfTheDay(startDate)}${
            this.campusTimeZone
          }`,
          [FILTER_KEYS.LESS_THAN_EQUAL]: `${getEndTimeOfTheDay(endDate)}${this.campusTimeZone}`,
        }
      } else if (this.filters.filterBy === 'Month') {
        const { startOfMonth, endOfMonth } = getMonthStartAndEndDate(startDate)
        startTime = {
          [FILTER_KEYS.GREATER_THAN_EQUAL]: `${getStartTimeOfTheDay(startOfMonth)}${
            this.campusTimeZone
          }`,
          [FILTER_KEYS.LESS_THAN_EQUAL]: `${getEndTimeOfTheDay(endOfMonth)}${this.campusTimeZone}`,
        }
      } else if (this.filters.filterBy === 'Day') {
        startTime = {
          [FILTER_KEYS.GREATER_THAN_EQUAL]: `${getStartTimeOfTheDay(startDate)}${
            this.campusTimeZone
          }`,
          [FILTER_KEYS.LESS_THAN_EQUAL]: `${getEndTimeOfTheDay(startDate)}${this.campusTimeZone}`,
        }
      }

      this.isLoading = true
      const payload = {
        $where: {
          ...this.filtersData?.$where,
          started_at: startTime,
        },
      }

      if (this.activeRole === TENANT_ROLES.SECTION_TEACHER && this.isMyCurrentTimeTable) {
        payload.$where.teacher_id = { $equal: this.currentUser?.id }
      }

      this.filterPeriod(payload)
        .then((resp) => {
          this.fetchPeriodsStats(payload)
          this.respDataObjDump = resp?.data?.records || []
          this.events =
            this.filters.filterBy === 'Month'
              ? resp?.data?.records.map((r) => getSubjectEventUtc(r))
              : getEventsWithNonOverlap(resp?.data?.records || [])
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    fetchPeriodsStats(filterBy) {
      this.getPeriodsStats(filterBy).then((response) => {
        this.updateRightbarContent(response?.data)
      })
    },
    updateRightbarContent(data) {
      const rightBarSubjectsList = [
        {
          title: this.$t('schedule.Subjects'),
          categories: [
            {
              text: 'Total Subject',
              value: data?.subjects_count,
              tooltipDescription: 'Total No of Subject',
            },
            {
              text: 'TOTAL_PERIODS',
              value: data?.periods_count,
              tooltipDescription: 'Lectures held today',
            },
            {
              text: 'TODAYS_PERIODS',
              value: data?.today_subjects_count,
              tooltipDescription: 'Lectures held today',
            },
          ],
        },
        ...(data?.total_subjects_with_detail || []).map((subject) => ({
          title: subject.subject_name,
          categories: [
            { text: 'Total Class', value: subject.periods_count },
            { text: 'Completed', value: subject.completed_periods_count },
          ],
          color: subject.subject_color,
        })),
      ]

      this.setRightbarContent({
        header: {
          title: 'PERIOD',
          buttons:
            this.currentStudent || this.isMyCurrentTimeTable
              ? []
              : [
                  {
                    title: 'ADD_CURRENT_TIME_TABLE',
                    classes: ['primary-button-right'],
                    action: { name: 'layout/setShowModal', payload: {} },
                  },
                ],
        },
        stats: rightBarSubjectsList,
      })
    },

    clickDay(date) {
      this.detailsEventsArr = this.events.filter((single) => single.date === date)
      this.PeriodDetailModal = true
    },
    singleRecord(record) {
      this.detailsEventsArr = [record]
      this.PeriodDetailModal = true
    },

    setFilter(filter) {
      const startDate = new Date()
      const endDate = new Date()
      endDate.setDate(endDate.getDate() + 6)
      this.dateRange = { startDate, endDate }
      this.filters.filterBy = filter
      this.applyFilter()
    },

    handleModalClose() {
      this.editEventData = null
    },

    handlePeriodSaved() {
      this.applyFilter()
    },

    viewEvent(eventId) {
      const event = this.events.find((e) => e.id === eventId)
      if (!event) return

      let detailsEventsArr = [event]
      if (event?.isOverlapped) {
        const overlappedEvents = event.overlappedIds.map((id) =>
          getSubjectEventUtc(this.respDataObjDump.find((obj) => obj.id === id)),
        )
        detailsEventsArr = [...detailsEventsArr, ...overlappedEvents]
      }
      this.detailsEventsArr = detailsEventsArr
      this.PeriodDetailModal = true
    },

    editEvent(event) {
      this.editEventData = event
      this.$store.dispatch('layout/setShowModal', true)
    },

    deleteEvent(event) {
      this.deletePeriod(event.id).then(() => {
        this.$store.commit('toast/NEW', {
          type: 'success',
          message: this.$t('toast.Schedule deleted successfully !'),
        })
        this.applyFilter()
      })
    },

    ...mapActions('layout', ['setRightbarContent']),
    ...mapActions('period', ['deletePeriod', 'filterPeriod', 'getPeriodsStats']),
  },
}
</script>
